import { useTheme } from '@mui/material';
import { MprcRequestErrorReport } from 'components';
import { MprcFireworks } from 'components/MprcFireworks';
import { EnrollmentViewDto, EnrollStatus, TrainingType } from "generated/CoreApi_v1";
import { useWindowSize } from 'hooks/useWindowSize';
import { enqueueSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from "react-router";
import { LmsPaths } from 'routes/LmsRouter';
import { NIL } from "uuid";
import { useGobackOrLms } from 'views/common/hooks/useGobackOrHome';
import { CardVideoSearchData, CardVideoSearchEvent, subscribeVideoSearchEvent, unsubscribeVideoSearchEvent } from "views/knowledge/components/card/CardEvents";
import { CourseEnrollmentView } from "./Course/CourseEnrollmentView";
import { ECourseEnrollmentView } from './ECourse/ECourseEnrollmentView';
import { LpEnrollmentView } from "./LearnPath/LpEnrollmentView";
import { EnrollmentViewBar } from "./Shared/EnrollmentViewBar";
import { EnrollmentAccess, useEnrollmentCheckAccessTime } from "./Shared/useEnrollmentCheckAccessTime";

/////////////////////////////////////////////////////////////////////////////////////

export type EnrollmentViewContextType = {

    enrollmentId: string;
    trainingType: TrainingType;
    enrollment: EnrollmentViewDto,

    videoSearch?: CardVideoSearchData,
    loading: boolean;
    openRightPanel: boolean;
    setOpenRightPanel: (value: boolean) => void;

    onReload: () => void;
    onError: (e: any) => void;
    onChangeEnrollment: (newData: Partial<EnrollmentViewDto>) => void;
};

export const EnrollmentViewContext = React.createContext<EnrollmentViewContextType>(undefined!);
export const useEnrollmentViewContext = () => React.useContext(EnrollmentViewContext);

/////////////////////////////////////////////////////////////////////////////////////

export const EnrollmentView: React.FC = () => {

    const { enrollmentId, trainingType } = useParams();

    const api = useCoreApi();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const { t } = useTranslation("lms");

    const [showFireworks, setShowFireworks] = useState<boolean>(false);
    const [navigating, setNavigating] = useState<boolean>(false);
    const [reloading, setReloading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const [enrollment, setEnrollment] = useState<EnrollmentViewDto>();
    const [videoSearch, setVideoSearch] = useState<CardVideoSearchData>();
    const [enrollmentAccess, setEnrollmentAccess] = useState<EnrollmentAccess>({ isBlocked: false });

    const { width } = useWindowSize({ wait: 50 });

    const { checkEnrollmentAccess } = useEnrollmentCheckAccessTime(
        enrollmentId!
        , enrollmentAccess.isBlocked
        , setEnrollmentAccess
        , () => setLoading(true));

    const goBack = useGobackOrLms();

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const updateVideoSearch = (data: CardVideoSearchEvent) => {
        setVideoSearch(data.detail);
    };

    //////////////////////////////////////////////////////
    /// EFFECT'S
    //////////////////////////////////////////////////////

    useEffect(() => {
        if (loading || reloading) {
            checkEnrollmentAccess()
                .then((canAccess) => {
                    if (canAccess) {

                        let request = null;
                        if (TrainingType[trainingType! as keyof typeof TrainingType] === TrainingType.Course) {
                            request = api.v1.enrollment.getCourseEnrollment({ enrollmentId: enrollmentId ?? NIL });
                        }
                        else if (TrainingType[trainingType! as keyof typeof TrainingType] === TrainingType.LearningPath) {
                            request = api.v1.enrollment.getLearnPathEnrollment({ enrollmentId: enrollmentId ?? NIL });
                        }
                        else if (TrainingType[trainingType! as keyof typeof TrainingType] === TrainingType.ExternalCourse) {
                            request = api.v1.enrollment.getECourseEnrollment({ enrollmentId: enrollmentId ?? NIL });
                        }

                        request?.then(response => {

                            //Usuário acabou de completar um componente que garantiu as condições de aprovação.
                            if (enrollment?.wasFinalized === false && response.data.wasFinalized === true) {

                                setShowFireworks(response.data.status === EnrollStatus.Approved);

                                enqueueSnackbar(
                                    response.data.status === EnrollStatus.Approved
                                        ? t("Student.EnrollmentView.ApprovedMessage")
                                        : t("Student.EnrollmentView.ReprovedMessage")
                                    , { variant: response.data.status === EnrollStatus.Approved ? "success" : "error", autoHideDuration: 5000 }
                                );
                            }

                            // Se turma não permite rever curso, volta para página Home do módulo de treinamento.
                            if (response.data.finalizationDate != null && response.data.canReview === false) {
                                navigate(LmsPaths.Home);
                            }
                            else {
                                setEnrollment(response.data);
                            }
                        })
                            .catch(e => setViewError(e))
                            .finally(() => { setLoading(false); setReloading(false); });
                    }
                    else {
                        if (reloading) {
                            // Após finalizar perdeu acesso ao treinamento, então retorna.
                            setNavigating(true);
                            goBack();
                        }
                        else {
                            setLoading(false);
                            setReloading(false);
                        }
                    }
                })
                .catch(e => setViewError(e));
        }
    }, [loading, reloading]);

    useEffect(() => {
        setViewError(null);
        setEnrollment(undefined);
        setOpenRightPanel(false);
        setLoading(true);
        setReloading(false);
    }, [location.pathname]);

    useEffect(() => {
        subscribeVideoSearchEvent(updateVideoSearch);
        return () => {
            unsubscribeVideoSearchEvent(updateVideoSearch);
        };
    }, []);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    //////////////////////////////////////////////////////
    /// RENDER
    //////////////////////////////////////////////////////

    return (
        <>
            {
                viewError != undefined
                    ? <MprcRequestErrorReport error={viewError} />
                    : <>
                        <EnrollmentViewContext.Provider
                            value={enrollment ?
                                {
                                    enrollmentId: enrollment.enrollmentId,
                                    trainingType: enrollment.trainingType,
                                    enrollment: enrollment,
                                    onChangeEnrollment: (newData) => setEnrollment(state => ({ ...state!, ...newData })),
                                    openRightPanel, setOpenRightPanel,
                                    loading: loading || reloading,
                                    videoSearch: videoSearch,
                                    onReload: () => setReloading(true),
                                    onError: (e) => setViewError(e),
                                }
                                : {} as EnrollmentViewContextType
                            }
                        >
                            <EnrollmentViewBar loading={loading || reloading} onClickRightPanel={() => setOpenRightPanel(!openRightPanel)} />
                            {enrollmentAccess.isBlocked
                                ? navigating ? null : enrollmentAccess.blockContent
                                : enrollment &&
                                <>
                                    {enrollment.trainingType == TrainingType.Course && <CourseEnrollmentView />}
                                    {enrollment.trainingType == TrainingType.ExternalCourse && <ECourseEnrollmentView />}
                                    {enrollment.trainingType == TrainingType.LearningPath && <LpEnrollmentView />}
                                </>
                            }
                        </EnrollmentViewContext.Provider>

                        <MprcFireworks
                            isActive={showFireworks}
                            duration={5000}
                            onComplete={() => setShowFireworks(false)}
                        />
                    </>
            }
        </>
    );
};
